import { useMemo } from 'react';
import { Tooltip } from '@mui/material';

import { DataTable, type TableColumn, EditIcon, TrashIcon } from '@/src/ui';
import { UsersIcon } from '@/src/ui/SVGIcons';
import { Constants, ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import { useAuth } from '@/src/app/context/useAuth';
import type { ClassDTO } from '../models/classDTOs';

interface ClassesTableProps {
    data: Array<ClassDTO>;
    onEdit?: (row: ClassDTO) => void;
    onDelete?: (row: ClassDTO) => void;
    emptyMessage?: string;
    onShowRoster?: (row: ClassDTO) => void;
}

export default function ClassesTable(props: ClassesTableProps) {
    const { data, onEdit, onDelete, emptyMessage, onShowRoster } = props;
    const { user } = useAuth();

    const columns = useMemo<TableColumn[]>(() => {
        if (!data || data.length === 0) return [];
        const cols: TableColumn[] = [
            {
                dataKey: 'classId',
                header: 'ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.classId,
            },
            {
                dataKey: 'className',
                header: 'Class Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.className,
            },
            {
                dataKey: 'schoolName',
                header: 'School',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.schoolName,
            },
            {
                dataKey: 'districtName',
                header: 'District',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.districtName,
            },
            {
                dataKey: 'gradeLevel',
                header: 'Grade Level',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.gradeLevel,
            },
            {
                dataKey: 'studentCount',
                header: 'Student Count',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.studentCount,
            },
            {
                dataKey: 'schoolYear',
                header: 'School Year',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.schoolYear,
            },
            {
                dataKey: 'status',
                header: 'Status',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ClassDTO) => row.status,
            },
        ];
        cols.push({
            dataKey: 'actions',
            header: 'Actions',
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: ClassDTO) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {onEdit && user?.role === Constants.ROLES.SUPER_ADMIN && (
                        <Tooltip title="Edit" arrow>
                            <button
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => onEdit(row)}
                            >
                                <EditIcon />
                            </button>
                        </Tooltip>
                    )}
                    {onDelete && user?.role === Constants.ROLES.SUPER_ADMIN && (
                        <Tooltip title="Delete" arrow>
                            <button
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => onDelete(row)}
                            >
                                <TrashIcon />
                            </button>
                        </Tooltip>
                    )}
                    {onShowRoster &&
                        (user?.role === Constants.ROLES.TEACHER ||
                            user?.role === Constants.ROLES.DISTRICT_ADMIN ||
                            user?.role === Constants.ROLES.SCHOOL_ADMIN) && (
                            <Tooltip title="Show Roster" arrow>
                                <button
                                    style={{
                                        background: 'var(--pm-accent)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'black',
                                        padding: '4px 10px',
                                        cursor: 'pointer',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                    onClick={() => onShowRoster?.(row)}
                                >
                                    <UsersIcon style={{ width: 18, height: 18 }} />
                                    Roster
                                </button>
                            </Tooltip>
                        )}
                </div>
            ),
        });
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, onEdit, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={data}
            tableName="Classes"
            rowsPerPageInitial={20}
            alternatingRowColor
            defaultSortColumn="className"
            defaultSortDirection="asc"
            emptyMessage={emptyMessage}
        />
    );
}
